/**
 * aiVideoRenderer.js — turn an AI video plan (scene images + per-scene narration
 * + background music) into a vertical 720x1280 WebM ready for Mux.
 *
 * renderAiVideo({ scenes, narrations, musicUrl, title, onProgress }) → Blob
 *   scenes:     [{ image_url, caption }]
 *   narrations: [AudioBuffer | null]  (one per scene, decoded)
 *   musicUrl:   same-origin stream URL or null
 */
import { recordVideo } from "./videoExporter";
import { fileUrl } from "./api";

const W = 720, H = 1280, MIN_SCENE = 3.2, GAP = 0.6, XFADE = 0.5, MAX_TOTAL = 90;

export async function decodeClip(url) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  const res = await fetch(fileUrl(url), { credentials: "include" });
  const buf = await ctx.decodeAudioData(await res.arrayBuffer());
  ctx.close().catch(() => {});
  return buf;
}

function loadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = fileUrl(url);
  });
}

/** Concatenate narration buffers at each scene's start offset into one AudioBuffer. */
function concatNarrations(narrations, starts, total) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const rate = narrations.find(Boolean)?.sampleRate || 44100;
  const ctx = new AC({ sampleRate: rate });
  const out = ctx.createBuffer(1, Math.ceil(total * rate), rate);
  const ch = out.getChannelData(0);
  narrations.forEach((buf, i) => {
    if (!buf) return;
    const src = buf.numberOfChannels > 1 ? mixToMono(buf) : buf.getChannelData(0);
    const offset = Math.floor((starts[i] + 0.25) * rate);
    for (let j = 0; j < src.length && offset + j < ch.length; j++) ch[offset + j] += src[j];
  });
  ctx.close().catch(() => {});
  return out;
}

function mixToMono(buf) {
  const n = buf.length, out = new Float32Array(n);
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < n; i++) out[i] += d[i] / buf.numberOfChannels;
  }
  return out;
}

export function sceneTimeline(scenes, narrations) {
  const durations = scenes.map((_, i) => Math.max(MIN_SCENE, (narrations[i]?.duration || 0) + GAP));
  let total = durations.reduce((a, b) => a + b, 0);
  if (total > MAX_TOTAL) { const k = MAX_TOTAL / total; durations.forEach((d, i) => { durations[i] = d * k; }); total = MAX_TOTAL; }
  const starts = []; let t = 0;
  durations.forEach((d) => { starts.push(t); t += d; });
  return { durations, starts, total };
}

function drawCover(ctx, img, zoom, panX) {
  const s = Math.max(W / img.width, H / img.height) * zoom;
  const w = img.width * s, h = img.height * s;
  ctx.drawImage(img, (W - w) / 2 + panX * (w - W) * 0.5, (H - h) / 2, w, h);
}

function wrap(ctx, text, max) {
  const words = text.split(/\s+/), lines = []; let line = "";
  words.forEach((wd) => { const t = line ? `${line} ${wd}` : wd; if (ctx.measureText(t).width > max && line) { lines.push(line); line = wd; } else line = t; });
  if (line) lines.push(line);
  return lines.slice(0, 3);
}

export async function renderAiVideo({ scenes, narrations, musicUrl, title, onProgress }) {
  const images = await Promise.all(scenes.map((s) => (s.image_url ? loadImage(s.image_url) : Promise.resolve(null))));
  const { durations, starts, total } = sceneTimeline(scenes, narrations);
  const voiceBuffer = narrations.some(Boolean) ? concatNarrations(narrations, starts, total) : null;

  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;

  const drawFrame = (cv, elapsed) => {
    const ctx = cv.getContext("2d");
    let idx = starts.findIndex((s, i) => elapsed >= s && elapsed < s + durations[i]);
    if (idx < 0) idx = scenes.length - 1;
    const local = elapsed - starts[idx];
    const prog = Math.min(1, local / durations[idx]);
    ctx.fillStyle = "#070A10"; ctx.fillRect(0, 0, W, H);
    const paint = (i, alpha, p) => {
      const img = images[i];
      ctx.globalAlpha = alpha;
      if (img) drawCover(ctx, img, 1.04 + 0.1 * p, i % 2 === 0 ? p : -p);
      else {
        const g = ctx.createLinearGradient(0, 0, W, H);
        g.addColorStop(0, "#0EA5E9"); g.addColorStop(1, "#1E3A8A");
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      }
      ctx.globalAlpha = 1;
    };
    paint(idx, 1, prog);
    if (idx > 0 && local < XFADE) paint(idx - 1, 1 - local / XFADE, 1);
    // bottom gradient + caption
    const grad = ctx.createLinearGradient(0, H * 0.62, 0, H);
    grad.addColorStop(0, "rgba(7,10,16,0)"); grad.addColorStop(1, "rgba(7,10,16,0.92)");
    ctx.fillStyle = grad; ctx.fillRect(0, H * 0.62, W, H * 0.38);
    const cap = scenes[idx].caption || "";
    if (cap) {
      ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillStyle = "#FFF4E0";
      ctx.font = '900 58px "Anton", "Arial Black", sans-serif';
      ctx.shadowColor = "rgba(0,0,0,0.6)"; ctx.shadowBlur = 16;
      const lines = wrap(ctx, cap.toUpperCase(), W - 120);
      const reveal = Math.min(1, local / 0.5);
      ctx.globalAlpha = reveal;
      lines.forEach((ln, i) => ctx.fillText(ln, W / 2, H * 0.8 + (i - (lines.length - 1) / 2) * 66 + (1 - reveal) * 30));
      ctx.globalAlpha = 1; ctx.shadowBlur = 0;
    }
    if (title && elapsed < 2.2) {
      ctx.globalAlpha = Math.min(1, elapsed / 0.4) * (elapsed > 1.7 ? Math.max(0, (2.2 - elapsed) / 0.5) : 1);
      ctx.fillStyle = "rgba(7,10,16,0.55)"; ctx.fillRect(0, H * 0.42, W, 150);
      ctx.fillStyle = "#38BDF8"; ctx.font = '900 54px "Anton", "Arial Black", sans-serif'; ctx.textAlign = "center";
      wrap(ctx, title.toUpperCase(), W - 100).slice(0, 2).forEach((ln, i) => ctx.fillText(ln, W / 2, H * 0.42 + 75 + (i - 0.5) * 60));
      ctx.globalAlpha = 1;
    }
    // AI badge + progress
    ctx.fillStyle = "rgba(255,244,224,0.85)"; ctx.font = '700 20px "Manrope", sans-serif'; ctx.textAlign = "right";
    ctx.fillText("✦ AI-GENERATED · GIFFWURLDAUNCE", W - 28, 48);
    ctx.fillStyle = "rgba(255,255,255,0.25)"; ctx.fillRect(0, H - 6, W, 6);
    ctx.fillStyle = "#38BDF8"; ctx.fillRect(0, H - 6, W * (elapsed / total), 6);
  };
  drawFrame(canvas, 0);

  const blob = await recordVideo({ canvas, duration: total, drawFrame, audioUrl: musicUrl, voiceBuffer, format: "webm", onProgress });
  return { blob, total };
}
